import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import {
  getBranchOptions,
  getInternalDevices,
  getPhoneModelOptions,
  getDeclaredFields,
} from '@/features/operations/api';
import { DirectDeviceForm } from '@/features/operations/components/DirectDeviceForm';
import { PendingRefundsAlert } from '@/features/operations/components/PendingRefundsAlert';
import {
  deviceStateOptions,
  formatQuotationDate,
  getDeviceStateLabel,
} from '@/features/operations/labels';
import type {
  BranchOption,
  DeviceItem,
  DeviceModelOption,
  PublicDeclaredField,
} from '@/features/operations/types';
import { StatusChip } from '@/components/StatusChip';

// Refetch delay for free-text search, so each keystroke doesn't round-trip to the API.
const SEARCH_DEBOUNCE_MS = 400;

/**
 * The phones PhoneTec has received. Each row opens the phone's own page: its internal sheet
 * and who offered it.
 */
export function DevicesPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const branchFilter = searchParams.get('branchId') || '';
  const stateFilter = searchParams.get('state') || '';
  const search = searchParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modelOptions, setModelOptions] = useState<DeviceModelOption[]>([]);
  const [declaredFields, setDeclaredFields] = useState<PublicDeclaredField[]>([]);
  const [entryOpen, setEntryOpen] = useState(false);

  /// Replaces the history entry instead of adding one, like the quotations list: going back
  /// should leave the page, not undo a filter one keystroke at a time.
  const setFilter = (key: string, value: string) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);

        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }

        return next;
      },
      { replace: true },
    );

  // Debounces the free-text search box; the two selects apply immediately, since they are
  // discrete choices instead of keystrokes.
  useEffect(() => {
    const timeout = setTimeout(() => {
      const trimmed = searchInput.trim();

      if (trimmed !== search) {
        setFilter('search', trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
    // `search` and `setFilter` are left out on purpose: this reacts to typing only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  /// The branches are the filter's options, so they are loaded once rather than per filter.
  useEffect(() => {
    if (!token) {
      return;
    }

    getBranchOptions(token)
      .then(setBranchOptions)
      .catch((error: unknown) => {
        setErrorMessage(
          error instanceof Error ? error.message : 'No pudimos cargar las sucursales.',
        );
      });
  }, [token]);

  useEffect(() => {
    if (!token) return;
    Promise.all([getPhoneModelOptions(token), getDeclaredFields()])
      .then(([models, fields]) => {
        setModelOptions(models);
        setDeclaredFields(fields);
      })
      .catch((error: unknown) =>
        setErrorMessage(
          error instanceof Error ? error.message : 'No pudimos cargar el formulario.',
        ),
      );
  }, [token]);

  useEffect(() => {
    const loadDevices = async () => {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const deviceItems = await getInternalDevices(token, {
          branchId: branchFilter || undefined,
          state: stateFilter || undefined,
          search: search || undefined,
        });
        setDevices(deviceItems);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'No pudimos cargar los equipos.');
      } finally {
        setIsLoading(false);
      }
    };

    void loadDevices();
  }, [token, branchFilter, stateFilter, search]);

  if (!token) {
    return null;
  }

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="EQUIPOS"
        title="Equipos recibidos"
        description="Los celulares que PhoneTec ya tuvo en mano, con su estado y su ficha interna."
        badge="Uso interno"
      />
      <PendingRefundsAlert token={token} />

      <Button
        variant="contained"
        color="secondary"
        onClick={() => setEntryOpen(true)}
        sx={{ alignSelf: 'flex-start' }}
      >
        Ingresar equipo
      </Button>
      <DirectDeviceForm
        open={entryOpen}
        token={token}
        models={modelOptions}
        branches={branchOptions}
        fields={declaredFields}
        onClose={() => setEntryOpen(false)}
        onCreated={(device) => {
          setEntryOpen(false);
          navigate(`/admin/devices/${device.id}`, {
            state: { from: searchParams.toString(), justCreated: true },
          });
        }}
      />
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <SectionCard
          title="Estado de equipos"
          description="El estado cambia solo con las acciones del flujo: publicar, despublicar, reservar y vender."
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar"
                placeholder="Marca, modelo, sucursal o cliente"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {/* Closed branches stay in the list: the phones received there are still theirs
                  to find. */}
              <TextField
                fullWidth
                select
                label="Filtrar por sucursal"
                value={branchFilter}
                onChange={(event) => setFilter('branchId', event.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {branchOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name} · {option.city}
                    {option.isActive ? '' : ' (cerrada)'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                select
                label="Filtrar por estado"
                value={stateFilter}
                onChange={(event) => setFilter('state', event.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {deviceStateOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {getDeviceStateLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {devices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {branchFilter || stateFilter || search
                ? 'No hay equipos que coincidan con el filtro elegido.'
                : 'Todavía no se recibió ningún equipo.'}
            </Typography>
          ) : (
            // Only the table scrolls sideways on a narrow screen, never the page.
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Recibido</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Equipo</TableCell>
                    <TableCell>Ofrecido por</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.map((device) => {
                    const deviceName = `${device.brand} ${device.model}`;
                    const sheetPath = `/admin/devices/${device.id}`;
                    // The way back returns to the same filters.
                    const sheetState = { from: searchParams.toString() };

                    return (
                      <TableRow
                        key={device.id}
                        hover
                        onClick={() => navigate(sheetPath, { state: sheetState })}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatQuotationDate(device.createdAt)}
                        </TableCell>
                        <TableCell>
                          {/* The row is clickable with a mouse; the link is the way in from
                              the keyboard and for screen readers. */}
                          <Link
                            component={RouterLink}
                            to={sheetPath}
                            state={sheetState}
                            aria-label={`Abrir ficha de ${deviceName}`}
                            underline="hover"
                            color="inherit"
                            fontWeight={600}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {deviceName}
                          </Link>
                          {device.storageGb === null && !device.color ? null : (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[
                                device.storageGb === null ? null : `${device.storageGb}GB`,
                                device.color?.name,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          )}
                        </TableCell>
                        {/* Who offered it is the contact on its quotation: the person who
                            quotes a phone is the person selling it. */}
                        <TableCell>
                          {device.quotation?.contact.name ??
                            (device.origin === 'DIRECT_ENTRY'
                              ? 'Ingreso directo'
                              : 'Sin cotización (registro anterior)')}
                        </TableCell>
                        <TableCell>
                          {/* Where it is standing. Null only for phones received before
                              branches existed. */}
                          <Typography variant="body2">
                            {device.branch
                              ? `${device.branch.name} · ${device.branch.city}`
                              : 'Sin sucursal (registro anterior)'}
                          </Typography>
                          {device.receivedBy ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Recibido por {device.receivedBy.fullName}
                            </Typography>
                          ) : null}
                        </TableCell>
                        {/* The state moves only through publish, unpublish and sell (ADR 0005):
                            the list names it and offers no way to set it by hand. */}
                        <TableCell>
                          <StatusChip domain="device" status={device.deviceState} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </SectionCard>
      )}
    </Stack>
  );
}
